/**
 * EntityManageMenu — reusable Archive/Restore + Delete controls for a party or
 * account, with a pre-delete "what's blocking deletion" modal.
 *
 * Delete intent calls the read-only `/blockers` pre-flight: if the entity is
 * deletable it offers a confirming hard-delete; otherwise it lists the itemized
 * blockers and offers Archive instead. Archive toggling is PIN-gated for
 * parties (POST archive/unarchive) and a plain status PATCH for accounts.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, ArchiveRestore, Trash2 } from 'lucide-react';
import {
  api,
  ApiError,
  formatBlockerReport,
  type BlockerReport,
} from '../api';
import { usePinGate } from '../auth/pinGate';
import { Button, ErrorBanner, Spinner } from '../ui/primitives';

export interface EntityManageMenuProps {
  /** REST collection segment. */
  kind: 'parties' | 'accounts';
  id: number;
  /** Human noun for copy, e.g. "client", "account", "upline". */
  noun: string;
  /** Whether the entity is currently archived/inactive. */
  isArchived: boolean;
  /** Archive/unarchive is PIN-gated (parties) vs a plain status PATCH (accounts). */
  archivePinGated: boolean;
  /** Called after any successful archive/unarchive/delete to refresh lists. */
  onChanged: () => void;
  /** Called after a successful hard-delete (e.g. navigate away). */
  onDeleted?: () => void;
}

export function EntityManageMenu({
  kind,
  id,
  noun,
  isArchived,
  archivePinGated,
  onChanged,
  onDeleted,
}: EntityManageMenuProps) {
  const { withPin } = usePinGate();
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const archiveMut = useMutation({
    mutationFn: () => {
      if (kind === 'parties') {
        return api.post(`/api/parties/${id}/${isArchived ? 'unarchive' : 'archive'}`);
      }
      // accounts: archive = set status 'inactive', restore = 'active'
      return api.patch(`/api/accounts/${id}`, {
        status: isArchived ? 'active' : 'inactive',
      });
    },
    onSuccess: () => {
      setError(null);
      onChanged();
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 423) return; // PIN gate re-runs
      setError(e instanceof ApiError ? e.message : String(e));
    },
  });

  const onToggleArchive = () => {
    setError(null);
    if (archivePinGated) void withPin(() => archiveMut.mutateAsync());
    else archiveMut.mutate();
  };

  const blockersQ = useQuery<BlockerReport>({
    queryKey: [kind, id, 'blockers'],
    queryFn: () => api.get<BlockerReport>(`/api/${kind}/${id}/blockers`),
    enabled: modalOpen,
  });

  const deleteMut = useMutation({
    mutationFn: () => api.delete(`/api/${kind}/${id}`),
    onSuccess: () => {
      setError(null);
      setModalOpen(false);
      void qc.invalidateQueries();
      onChanged();
      onDeleted?.();
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 423) return;
      setError(e instanceof ApiError ? e.message : String(e));
    },
  });

  const busy = archiveMut.isPending || deleteMut.isPending;

  return (
    <>
      <div className="flex gap-2">
        <Button
          variant="ghost"
          size="sm"
          className="flex-1 justify-center"
          disabled={busy}
          onClick={onToggleArchive}
          title={isArchived ? `Restore this ${noun}` : `Hide this ${noun} from active lists (reversible)`}
        >
          {isArchived ? (
            <>
              <ArchiveRestore className="h-3.5 w-3.5" strokeWidth={2} /> Restore
            </>
          ) : (
            <>
              <Archive className="h-3.5 w-3.5" strokeWidth={2} /> Archive
            </>
          )}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="flex-1 justify-center text-neg hover:bg-neg/5"
          disabled={busy}
          onClick={() => {
            setError(null);
            setModalOpen(true);
          }}
        >
          <Trash2 className="h-3.5 w-3.5" strokeWidth={2} /> Delete
        </Button>
      </div>

      {error && (
        <div className="mt-2">
          <ErrorBanner>{error}</ErrorBanner>
        </div>
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/50 p-0 backdrop-blur-sm sm:items-center sm:p-4">
          <div className="flex w-full max-w-md flex-col overflow-hidden rounded-t-2xl border border-border bg-surface shadow-elevated sm:rounded-xl">
            <header className="flex items-center justify-between border-b border-border px-4 py-3">
              <h2 className="text-base font-semibold text-ink">Delete this {noun}?</h2>
              <Button size="sm" variant="ghost" onClick={() => setModalOpen(false)}>
                Close
              </Button>
            </header>
            <div className="space-y-3 p-4">
              {blockersQ.isLoading ? (
                <div className="flex items-center gap-2 text-small text-ink-muted">
                  <Spinner /> Checking…
                </div>
              ) : blockersQ.data?.deletable ? (
                <>
                  <p className="text-small text-ink">
                    This {noun} has no linked records. Deleting is permanent and cannot be undone.
                  </p>
                  {error && <ErrorBanner>{error}</ErrorBanner>}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="danger"
                      disabled={deleteMut.isPending}
                      onClick={() => void withPin(() => deleteMut.mutateAsync())}
                    >
                      {deleteMut.isPending ? <Spinner /> : 'Delete permanently'}
                    </Button>
                    <Button variant="ghost" onClick={() => setModalOpen(false)}>
                      Cancel
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-small text-ink">
                    This {noun} can&apos;t be deleted — it&apos;s still linked to{' '}
                    <span className="font-medium">
                      {blockersQ.data ? formatBlockerReport(blockersQ.data) : '…'}
                    </span>
                    . You can <strong>archive</strong> it instead — it stays hidden from your
                    active lists but keeps all its history, and you can restore it anytime.
                  </p>
                  {error && <ErrorBanner>{error}</ErrorBanner>}
                  <div className="flex items-center gap-2">
                    {!isArchived && (
                      <Button
                        disabled={busy}
                        onClick={() => {
                          onToggleArchive();
                          setModalOpen(false);
                        }}
                      >
                        <Archive className="h-4 w-4" strokeWidth={2} /> Archive instead
                      </Button>
                    )}
                    <Button variant="ghost" onClick={() => setModalOpen(false)}>
                      Cancel
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
